import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Edit3,
  Rocket,
  Bot,
  Database,
  Sparkles,
  MessageCircle,
  TestTube,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Image,
  Loader2,
  BookOpen,
  PlayCircle,
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';

// Icon mapping
const ICONS = {
  Rocket,
  Bot,
  Database,
  Sparkles,
  MessageCircle,
  TestTube,
  BarChart3,
  BookOpen,
  PlayCircle,
};

// Section defaults for icon and color
const SECTION_DEFAULTS = {
  'getting-started': { icon: 'Rocket', color: 'bg-green-500' },
  'agent-setup': { icon: 'Bot', color: 'bg-blue-500' },
  'knowledge-base': { icon: 'Database', color: 'bg-purple-500' },
  'features': { icon: 'Sparkles', color: 'bg-yellow-500' },
  'whatsapp': { icon: 'MessageCircle', color: 'bg-green-600' },
  'testing': { icon: 'PlayCircle', color: 'bg-orange-500' },
  'analytics': { icon: 'BarChart3', color: 'bg-cyan-500' },
};

export default function DocsList() {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.getDocsStructure();
      if (response.sections && response.sections.length > 0) {
        setSections(response.sections);
      }
    } catch (err) {
      console.error('Failed to fetch docs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Documentation</h1>
          <p className="text-text-secondary mt-1">
            Manage help articles and documentation for users
          </p>
        </div>
        <button
          onClick={() => navigate('/docs/editor')}
          className="btn-primary"
        >
          <Edit3 className="w-4 h-4" />
          Edit All
        </button>
      </div>

      {/* Documentation Sections */}
      <div className="card">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold text-text-primary">Documentation Sections</h2>
          <p className="text-sm text-text-muted mt-1">
            Click on a section to view articles, or click "Edit All" to edit everything
          </p>
        </div>

        {isLoading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sections.map((section) => {
              // Determine icon and color based on section title
              let IconComponent = FileText;
              let sectionColor = 'bg-primary';

              const title = section.title?.toLowerCase() || '';
              if (title.includes('getting started')) {
                IconComponent = Rocket;
                sectionColor = 'bg-green-500';
              } else if (title.includes('agent')) {
                IconComponent = Bot;
                sectionColor = 'bg-blue-500';
              } else if (title.includes('knowledge')) {
                IconComponent = Database;
                sectionColor = 'bg-purple-500';
              } else if (title.includes('feature')) {
                IconComponent = Sparkles;
                sectionColor = 'bg-yellow-500';
              } else if (title.includes('whatsapp')) {
                IconComponent = MessageCircle;
                sectionColor = 'bg-green-600';
              } else if (title.includes('test')) {
                IconComponent = PlayCircle;
                sectionColor = 'bg-orange-500';
              } else if (title.includes('analytics') || title.includes('report')) {
                IconComponent = BarChart3;
                sectionColor = 'bg-cyan-500';
              }

              const isExpanded = expandedSections[section.id];

              return (
                <div key={section.id}>
                  {/* Section Header */}
                  <div
                    className="p-4 flex items-center gap-4 hover:bg-bg-secondary/50 transition-colors cursor-pointer"
                    onClick={() => toggleSection(section.id)}
                  >
                    <div className={`w-10 h-10 rounded-lg ${sectionColor} flex items-center justify-center flex-shrink-0`}>
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-text-primary">{section.title}</h3>
                      <p className="text-sm text-text-muted">
                        {section.articles?.length || 0} article{section.articles?.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-text-muted" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-text-muted" />
                    )}
                  </div>

                  {/* Articles Dropdown */}
                  {isExpanded && section.articles && (
                    <div className="bg-bg-secondary/30 border-t border-border">
                      {section.articles.map((article, index) => (
                        <div
                          key={article.id}
                          className="px-4 py-3 pl-16 flex items-center justify-between hover:bg-bg-secondary/50 transition-colors cursor-pointer border-b border-border/50 last:border-b-0"
                          onClick={() => navigate('/docs/editor')}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-text-muted font-medium w-5">
                              {index + 1}.
                            </span>
                            <span className="text-text-primary">{article.title}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {article.screenshot && (
                              <span className="text-xs text-success flex items-center gap-1">
                                <Image className="w-3 h-3" />
                                Screenshot
                              </span>
                            )}
                            <Edit3 className="w-4 h-4 text-text-muted" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="mt-6 card p-4 bg-primary/5 border-primary/20">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-text-primary">About Documentation</h3>
            <p className="text-sm text-text-secondary mt-1">
              This documentation is displayed to users in the main app to help them understand
              how to use Botibonga Studio. You can edit article content, titles, and add
              screenshots to make the documentation more helpful.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
